import { isStablecoinSymbol } from '../classification'
import type { MulticallRetryFunction } from '../../types'
import {
  fetchGmxRawData,
  GMX_DEFAULT_APY_PERIOD,
  type GmxApyEntry,
} from './api'
import { getGmxApiHost } from './registry'
import {
  fetchGmxTickerPrices,
  priceGlvVaults,
  priceGmMarkets,
  type GlvMarketCap,
} from './pricing'
import type { GmxVault, GmxVaults, GmxVaultsFetchOptions } from './types'

/** GM and GLV pool tokens are 18-decimal on GMX V2. */
const GMX_TOKEN_DECIMALS = 18

const num = (n: number | undefined): number =>
  typeof n === 'number' && Number.isFinite(n) ? n : 0

/** The smaller of two optionally-present bounds (`undefined` ⇒ unbounded). */
const minDefined = (
  a: number | undefined,
  b: number | undefined,
): number | undefined => (a == null ? b : b == null ? a : Math.min(a, b))

/**
 * A GLV's remaining room (USD) to hold more GM of one market, from its
 * per-market caps (`GLV_MAX_MARKET_TOKEN_BALANCE_USD/_AMOUNT`) and current
 * holding. `undefined` when neither cap is configured (GLV-unbounded — only
 * the market's own pool cap then binds).
 */
const glvMarketRoomUsd = (
  cap: GlvMarketCap,
  priceUsd: number,
): number | undefined => {
  const currentUsd = (Number(cap.balanceAmount) / 1e18) * priceUsd
  const usdRoom =
    cap.maxBalUsd > 0n
      ? Math.max(0, Number(cap.maxBalUsd) / 1e30 - currentUsd)
      : undefined
  const amountRoom =
    cap.maxBalAmount > 0n
      ? Math.max(0, Number(cap.maxBalAmount - cap.balanceAmount) / 1e18) *
        priceUsd
      : undefined
  return minDefined(usdRoom, amountRoom)
}

/**
 * Denomination is `stable` only when BOTH legs are stablecoins (e.g. a
 * USDC/USDT pool). Any volatile long leg (ETH, BTC, …) makes the pool's
 * NAV track crypto, so it is `volatile` even though the short leg is USDC.
 */
const denominationFor = (
  longSymbol: string | undefined,
  shortSymbol: string | undefined,
): 'stable' | 'volatile' =>
  isStablecoinSymbol(longSymbol) && isStablecoinSymbol(shortSymbol)
    ? 'stable'
    : 'volatile'

/**
 * Fetch the GMX GM-market and GLV-vault listing for a chain from the
 * gmxinfra REST API. Returns a map keyed by lowercased token address.
 *
 * Each entry merges the `/apy` yield numbers with name + leg metadata from
 * `/markets` / `/glvs` and leg symbols from `/tokens`. Markets/GLVs that
 * report APY but lack metadata still surface (with a fallback name). GMX
 * vaults are always classified `volatile`; denomination follows the legs.
 *
 * Unsupported chains resolve to an empty map (not an error), so the universal
 * vault fetcher can request `gmx` on every chain without branching.
 *
 * When `multicallRetry` is supplied, each entry is additionally enriched with
 * `priceUsd` + `tvlUsd` via `Reader.getMarketTokenPrice` /
 * `GlvReader.getGlvTokenPrice` (fed by `/prices/tickers`). Pricing is
 * best-effort — a failure leaves the USD fields absent but never throws.
 */
export const fetchGmxVaults = async (
  chainId: string,
  multicallRetry?: MulticallRetryFunction,
  options?: GmxVaultsFetchOptions,
): Promise<GmxVaults> => {
  if (!options?.apiUrl && !getGmxApiHost(chainId)) return {}

  const period = options?.period ?? GMX_DEFAULT_APY_PERIOD
  const { apy, markets, glvs, tokens } = await fetchGmxRawData(
    chainId,
    period,
    options?.apiUrl,
  )

  // address (lowercased) → symbol
  const symbolOf = new Map<string, string>()
  for (const t of tokens) symbolOf.set(t.address.toLowerCase(), t.symbol)

  const out: GmxVaults = {}

  const build = (
    address: string,
    name: string,
    kind: GmxVault['kind'],
    longToken: string,
    shortToken: string,
    indexToken: string | undefined,
    entry: GmxApyEntry | undefined,
  ): GmxVault => {
    const longSymbol = symbolOf.get(longToken.toLowerCase())
    const shortSymbol = symbolOf.get(shortToken.toLowerCase())
    return {
      address: address.toLowerCase(),
      name,
      kind,
      symbol: kind === 'glv' ? 'GLV' : 'GM',
      decimals: GMX_TOKEN_DECIMALS,
      longToken: longToken.toLowerCase(),
      shortToken: shortToken.toLowerCase(),
      indexToken: indexToken ? indexToken.toLowerCase() : undefined,
      longSymbol,
      shortSymbol,
      apy: num(entry?.apy),
      baseApy: num(entry?.baseApy),
      bonusApr: num(entry?.bonusApr),
      yieldProfile: 'volatile',
      denomination: denominationFor(longSymbol, shortSymbol),
    }
  }

  // GM markets — driven by metadata, enriched with APY where present.
  for (const m of markets) {
    if (!m.isListed) continue
    const key = m.marketToken.toLowerCase()
    out[key] = build(
      m.marketToken,
      m.name,
      'gm',
      m.longToken,
      m.shortToken,
      m.indexToken,
      apy.markets?.[m.marketToken] ?? apy.markets?.[key],
    )
  }

  // GLV vaults — same treatment from the `/glvs` metadata.
  for (const g of glvs) {
    if (!g.isListed) continue
    const key = g.glvToken.toLowerCase()
    out[key] = build(
      g.glvToken,
      g.name,
      'glv',
      g.longToken,
      g.shortToken,
      undefined,
      apy.glvs?.[g.glvToken] ?? apy.glvs?.[key],
    )
  }

  // On-chain USD enrichment (best-effort). Prices each GM market via
  // `getMarketTokenPrice` and each GLV via `getGlvTokenPrice`, fed by the
  // gmxinfra oracle ticker prices. Skipped entirely without a multicaller.
  if (multicallRetry) {
    try {
      const prices = await fetchGmxTickerPrices(chainId, options?.apiUrl)
      if (prices.size > 0) {
        const listedMarkets = markets.filter((m) => m.isListed)
        // market address (lowercased) → index token, for GLV per-market pricing
        const marketIndexToken = new Map<string, string>()
        for (const m of listedMarkets)
          marketIndexToken.set(m.marketToken.toLowerCase(), m.indexToken)

        const [gmValues, glv] = await Promise.all([
          priceGmMarkets(
            chainId,
            multicallRetry,
            listedMarkets.map((m) => ({
              marketToken: m.marketToken,
              indexToken: m.indexToken,
              longToken: m.longToken,
              shortToken: m.shortToken,
            })),
            prices,
          ),
          priceGlvVaults(
            chainId,
            multicallRetry,
            glvs.filter((g) => g.isListed).map((g) => g.glvToken),
            prices,
            marketIndexToken,
          ),
        ])

        for (const [addr, v] of gmValues) {
          const e = out[addr]
          if (e) {
            e.priceUsd = v.priceUsd
            e.tvlUsd = v.tvlUsd
            e.liquidityUsd = v.liquidityUsd
            e.depositCapacityUsd = v.depositCapacityUsd
          }
        }
        for (const [addr, v] of glv.values) {
          const e = out[addr]
          if (!e) continue
          e.priceUsd = v.priceUsd
          e.tvlUsd = v.tvlUsd

          // GLV withdrawal liquidity (approximate): apply the constituent
          // markets' blended liquidity ratio (Σ liquidity / Σ TVL) to the
          // GLV's own value, since a GLV redeems through its GM markets.
          const markets = glv.glvMarkets.get(addr) ?? []
          let liqSum = 0
          let tvlSum = 0
          for (const m of markets) {
            const gv = gmValues.get(m)
            if (gv?.liquidityUsd != null && gv.tvlUsd != null) {
              liqSum += gv.liquidityUsd
              tvlSum += gv.tvlUsd
            }
          }
          if (tvlSum > 0 && v.tvlUsd != null)
            e.liquidityUsd = v.tvlUsd * (liqSum / tvlSum)

          // GLV deposit capacity (approximate): a GLV deposit routes into one
          // of its GM markets, bounded by BOTH that market's remaining pool
          // capacity and the GLV's own per-market GM-balance cap. Sum, per
          // market, the tighter of the two.
          const caps = glv.glvCaps.get(addr) ?? []
          let capacity = 0
          let known = false
          for (const cap of caps) {
            const gv = gmValues.get(cap.market)
            if (!gv) continue
            const marketRoom = gv.depositCapacityUsd
            const glvRoom = glvMarketRoomUsd(cap, gv.priceUsd)
            const room = minDefined(marketRoom, glvRoom)
            if (room != null) {
              capacity += room
              known = true
            }
          }
          if (known) e.depositCapacityUsd = capacity
        }
      }
    } catch {
      // Pricing is best-effort; the API listing stands on its own.
    }
  }

  return out
}
